import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SectionDefinitionService } from '../../../services/section-definition.service';
import { ToastrService } from 'ngx-toastr';
import {
  FilterProperty,
  FilterPropertyKey,
  SectionDefinition,
  SectionDefinitionEntityType,
  SectionDefinitionFilter,
  SortTypeSecDef,
} from '../../../models/section-definition.model';
import { EntityType } from '../../../enums/entity-type';

@Component({
  selector: 'lib-crud-section-definitions',
  templateUrl: './crud-section-definitions.component.html',
  styleUrls: ['./crud-section-definitions.component.scss'],
  standalone: false,
})
export class CrudSectionDefinitionsComponent implements OnInit {
  constructor(
    private sectionDefinitionService: SectionDefinitionService,
    private toastr: ToastrService
  ) {}

  FilterProperty = FilterProperty;

  count: number = 0;
  pageSize = 10;
  currentPage: number = 1;
  filter: SectionDefinitionFilter = new SectionDefinitionFilter();
  sortBy: SortTypeSecDef = SortTypeSecDef.NEWEST;
  SortType = SortTypeSecDef;

  mapSortBy: Record<number, string> = {
    [SortTypeSecDef.NEWEST]: 'Newest',
    [SortTypeSecDef.OLDEST]: 'Oldest',
    [SortTypeSecDef.DEVICE_NAME_ASC]: 'Device Name ↑',
    [SortTypeSecDef.DEVICE_NAME_DESC]: 'Device Name ↓',
  };

  entityTypeLabels: Record<string, string> = {
    LvPole: 'LV Pole',
    DistributionSubstation: 'Distribution Substation',
    MvPole: 'MV Pole',
    InjectionSubstation: 'Injection Substation',
    TransmissionStation: 'Transmission Station',
    HvPole: 'HV Pole',
  };

  get sortByName(): string {
    return this.mapSortBy[this.sortBy];
  }

  toggleSortDropdown = false;

  selectSortByOption(option: SortTypeSecDef) {
    this.sortBy = option;
    this.reloadTable();
    this.toggleSortDropdown = false;
  }

  sectionDefinitions: SectionDefinition[] = [];

  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  openAddModal(){
    this.showAddNewModal = true;
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
    };
    this.getAll(obj);
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);

    this.reloadTable();
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }

  private getFilterObject(): SectionDefinitionFilter {
    const obj: SectionDefinitionFilter = { ...this.filter };
    obj.sortBy = this.sortBy;

    if (obj.filterBy === FilterPropertyKey.ASSET_TYPE) {
      const entityType = this.getEntityTypeFromString(this.filter.search);
      if (entityType !== null) {
        obj.search = entityType.toString();
      } else {
        obj.search = '-1';
      }
    } else {
      obj.search = this.filter.search || '';
    }

    return obj;
  }

  onSearchEnter() {
    this.currentPage = 1;
    this.reloadTable();
  }

  private getAll(obj: any) {
    this.sectionDefinitionService.getAllForSelect(obj).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.count = responseData.data.count ?? this.count;
          this.sectionDefinitions = responseData?.data?.data?.length
            ? responseData?.data?.data?.map(
                (el: any) => new SectionDefinition(el)
              )
            : [];
          if (responseData.message !== '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  openDropdownIndex: number | null = null;

  onDropdownToggle(index: number) {
    this.openDropdownIndex = this.openDropdownIndex === index ? null : index;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    this.openDropdownIndex = null;
  }

  get filterOptions(): { key: FilterPropertyKey; label: string }[] {
    return [
      { key: FilterPropertyKey.NAME, label: FilterProperty.NAME },
      { key: FilterPropertyKey.ASSET_TYPE, label: FilterProperty.ASSET_TYPE },
    ];
  }

  private getEntityTypeFromString(input: string): SectionDefinitionEntityType | null {
    const normalizedInput = input.trim().toLowerCase().replace(/\s+/g, '');

    for (const key of Object.keys(SectionDefinitionEntityType)) {
      if (isNaN(Number(key))) {
        if (key.toLowerCase().replace(/\s+/g, '') === normalizedInput) {
          return SectionDefinitionEntityType[key as keyof typeof SectionDefinitionEntityType];
        }
      }
    }
    return null;
  }

  showAddNewModal: boolean = false;

  showReorderModal = false;
  onCloseReorderModal() {
    this.showReorderModal = false;
  }

  openReorderModal() {
    this.showReorderModal = true;
  }

  onCloseAddNewModal(){
    this.showAddNewModal = false;
  }


  onSectionDefinitionSaved(){
    this.reloadTable();
  }

  downloadSectionDefinition(){
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
    };

    this.sectionDefinitionService.downloadSectionDefinition(obj).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          window.location.href = responseData.data;
          if (responseData.message !== '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }
}
